/**
 * TanStack Query key factory (`src/rc` = React Query cache layer for this app).
 *
 * Conventions:
 * - Authenticated tenant data: [domain, restaurantId, ...params] so switches stay isolated.
 * - Public/unauthenticated: suffix includes the tenant slug from the URL `:slug` param where applicable.
 */

export const queryKeys = {
  categories: {
    all: (restaurantId: string) => ["categories", restaurantId] as const,
    active: (restaurantId: string) =>
      ["categories-active", restaurantId] as const,
  },
  /** Admin `/admin/menu`: full tenant menu entities (incl. inactive rows). */
  menuAdmin: {
    categories: (restaurantId: string) =>
      ["menu-admin", "categories", restaurantId] as const,
    dishes: (restaurantId: string) =>
      ["menu-admin", "dishes", restaurantId] as const,
    addOns: (restaurantId: string) =>
      ["menu-admin", "add-ons", restaurantId] as const,
    addOnCategories: (restaurantId: string) =>
      ["menu-admin", "add-on-categories", restaurantId] as const,
    ingredients: (restaurantId: string) =>
      ["menu-admin", "ingredients", restaurantId] as const,
    subcategories: (restaurantId: string) =>
      ["menu-admin", "subcategories", restaurantId] as const,
    tags: (restaurantId: string) =>
      ["menu-admin", "tags", restaurantId] as const,
    vatRates: (restaurantId: string) =>
      ["menu-admin", "vat-rates", restaurantId] as const,
    /** Tags with `type = category` (category card picker in CategoryDialog). */
    tagsCategoryType: (restaurantId: string) =>
      ["menu-admin", "tags-category-type", restaurantId] as const,
    /** `category_tags.tag_id` list for one category; ends with `restaurantId` for menu-admin invalidation. */
    categoryTagsForCategory: (restaurantId: string, categoryId: string) =>
      ["menu-admin", "category-tags", categoryId, restaurantId] as const,
    dishIngredientIds: (restaurantId: string, dishId: string) =>
      ["menu-admin", "dish-ingredient-ids", dishId, restaurantId] as const,
    dishSubcategoryIds: (restaurantId: string, dishId: string) =>
      ["menu-admin", "dish-subcategory-ids", dishId, restaurantId] as const,
    dishTagIds: (restaurantId: string, dishId: string) =>
      ["menu-admin", "dish-tag-ids", dishId, restaurantId] as const,
    /** `dish_add_ons.add_on_id` list for one dish (individual add-on selection). */
    dishAddOnIds: (restaurantId: string, dishId: string) =>
      ["menu-admin", "dish-addon-ids", dishId, restaurantId] as const,
  },
  dishes: {
    filtered: (
      restaurantId: string,
      opts: Record<string, unknown> | undefined
    ) => ["dishes", restaurantId, opts ?? {}] as const,
  },
  /** Admin menu appearance (`menu_style_settings`). */
  menuStyle: {
    settings: (restaurantId: string) =>
      ["menu-style-settings", restaurantId] as const,
  },
  /** Admin catering / events (`events` table). */
  events: {
    admin: (restaurantId: string) => ["events-admin", restaurantId] as const,
  },
  /** Admin suppliers CRM */
  suppliers: {
    admin: (restaurantId: string) =>
      ["suppliers-admin", restaurantId] as const,
  },
  /** Fortnox integration rows */
  fortnox: {
    settings: (restaurantId: string) =>
      ["fortnox-settings", restaurantId] as const,
    syncLogs: (restaurantId: string) =>
      ["fortnox-sync-logs", restaurantId] as const,
  },
  /** Admin `/admin/inventory`: dishes with category names + active categories for filters. */
  inventory: {
    bundle: (restaurantId: string) =>
      ["inventory", "bundle", restaurantId] as const,
    /** Per-dish scheduled stock rules (`inventory_schedules`). */
    schedules: (restaurantId: string, dishId: string) =>
      ["inventory", "schedules", restaurantId, dishId] as const,
  },
  menus: {
    all: (restaurantId: string) => ["timestamp-menus", restaurantId] as const,
    dishes: (restaurantId: string, menuId: string | null) =>
      ["menu-dishes", restaurantId, menuId] as const,
  },
  reservations: {
    byDate: (restaurantId: string, date: string) =>
      ["reservations", restaurantId, date] as const,
    range: (restaurantId: string, start: string, end: string) =>
      ["reservations-range", restaurantId, start, end] as const,
    list: (restaurantId: string, start: string, end: string) =>
      ["reservations-list", restaurantId, start, end] as const,
    allWindow: (restaurantId: string) =>
      ["reservations-all", restaurantId] as const,
    tables: (restaurantId: string) =>
      ["tables-reservations", restaurantId] as const,
    typesAdmin: (restaurantId: string) =>
      ["reservation-types-admin", restaurantId] as const,
    settings: (restaurantId: string) =>
      ["reservation-settings", restaurantId] as const,
  },
  dashboard: {
    orders: (restaurantId: string) =>
      ["dashboard-orders", restaurantId] as const,
    popularDishes: (restaurantId: string) =>
      ["dashboard-popular-dishes", restaurantId] as const,
    payments: (restaurantId: string) =>
      ["dashboard-payments", restaurantId] as const,
    reservationsToday: (restaurantId: string) =>
      ["dashboard-reservations-today", restaurantId] as const,
  },
  /** Admin `/admin/delivery-tracking`: `delivery_tracking` rows with order join */
  deliveryTracking: {
    list: (restaurantId: string) =>
      ["delivery-tracking", restaurantId] as const,
  },
  analytics: {
    orders: (restaurantId: string, range: string) =>
      ["analytics-orders", restaurantId, range] as const,
    payments: (restaurantId: string, range: string) =>
      ["analytics-payments", restaurantId, range] as const,
    dishes: (restaurantId: string) =>
      ["analytics-dishes", restaurantId] as const,
    categories: (restaurantId: string) =>
      ["analytics-categories", restaurantId] as const,
    automatedReports: (restaurantId: string) =>
      ["automated-reports", restaurantId] as const,
  },
  /** `/admin/reports` hub — KPIs, breakdowns, recent Z rows (TanStack Query). */
  reports: {
    hub: (restaurantId: string, period: string, lang: string) =>
      ["reports-hub", restaurantId, period, lang] as const,
    /** Tenant-scoped `automated_report_schedules` rows (Reports → Automated). */
    automatedSchedules: (restaurantId: string) =>
      ["reports-automated-schedules", restaurantId] as const,
  },
  /** Admin email campaigns + templates (`/admin/...` EmailCampaigns page). */
  emailCampaigns: {
    campaigns: (restaurantId: string) =>
      ["email-campaigns", restaurantId] as const,
    templates: (restaurantId: string) =>
      ["email-campaign-templates", restaurantId] as const,
    recipients: (restaurantId: string, campaignId: string) =>
      ["email-campaign-recipients", restaurantId, campaignId] as const,
    customersImport: (restaurantId: string) =>
      ["email-campaign-customers-import", restaurantId] as const,
  },
  /** Admin automations / notification templates (`automations`, `automation_logs`). */
  automations: {
    list: (restaurantId: string) => ["automations", restaurantId] as const,
    logs: (restaurantId: string, automationId: string) =>
      ["automation-logs", restaurantId, automationId] as const,
  },
  /** Admin `/admin` social media planner (`social_media_*`). */
  socialMedia: {
    accounts: (restaurantId: string) =>
      ["social-media-accounts", restaurantId] as const,
    posts: (restaurantId: string) =>
      ["social-media-posts", restaurantId] as const,
  },
  zReports: {
    savedList: (restaurantId: string) =>
      ["z-reports-saved-list", restaurantId] as const,
    detail: (restaurantId: string, reportId: string) =>
      ["z-report-detail", restaurantId, reportId] as const,
    todayBootstrap: (restaurantId: string, dateStr: string) =>
      ["z-report-today", restaurantId, dateStr] as const,
    /** Prefix for `invalidateQueries({ exact: false })` after save. */
    pageViewRoot: (restaurantId: string) =>
      ["z-report-page-view", restaurantId] as const,
    pageView: (restaurantId: string, selectionKey: string, lang: string) =>
      ["z-report-page-view", restaurantId, selectionKey, lang] as const,
    restaurantHeader: (restaurantId: string) =>
      ["z-report-restaurant-header", restaurantId] as const,
    serialCount: (restaurantId: string) =>
      ["z-report-serial-count", restaurantId] as const,
  },
  overview: {
    reservationsToday: (restaurantId: string, date: string) =>
      ["overview-reservations-today", restaurantId, date] as const,
    reservationsUpcoming: (restaurantId: string, from: string, to: string) =>
      ["overview-reservations-upcoming", restaurantId, from, to] as const,
    tables: (restaurantId: string) =>
      ["overview-tables", restaurantId] as const,
  },
  invoices: {
    /** Admin invoices page (manual fetch today; key used for invalidation on tenant switch) */
    adminList: (restaurantId: string) =>
      ["invoices-management", restaurantId] as const,
    /** Invoice dialog: lines for one invoice */
    dialogItems: (invoiceId: string) =>
      ["invoice-dialog-items", invoiceId] as const,
    /** Invoice dialog: pending orders to attach (tenant-scoped) */
    dialogPendingOrders: (restaurantId: string) =>
      ["invoice-dialog-pending-orders", restaurantId] as const,
    /** Create-invoice dialog: B2B customers with company name (tenant-scoped) */
    createCompanyCustomers: (restaurantId: string) =>
      ["invoice-create-company-customers", restaurantId] as const,
  },
  /** Admin customers list (CRM) */
  customers: {
    list: (restaurantId: string) => ["customers-admin", restaurantId] as const,
    /** Prefix for customer detail sheet queries — invalidate this to refresh all tabs */
    adminDetailBase: (customerId: string) =>
      ["customer-admin-detail", customerId] as const,
    /** Customer sheet: recent orders (tenant + customer scoped) */
    adminOrders: (restaurantId: string, customerId: string) =>
      ["customer-admin-detail", customerId, "orders", restaurantId] as const,
    /** Customer sheet: invoices for tenant + customer */
    adminInvoices: (restaurantId: string, customerId: string) =>
      ["customer-admin-detail", customerId, "invoices", restaurantId] as const,
    /** Customer sheet: loyalty member, tier, recent point transactions */
    adminLoyalty: (customerId: string) =>
      ["customer-admin-detail", customerId, "loyalty"] as const,
    /** Admin → Customer reviews (tenant-scoped) */
    reviews: (restaurantId: string) =>
      ["customer-reviews", restaurantId] as const,
  },
  /** Admin loyalty: redemptions ledger, members, etc. */
  loyalty: {
    /** Loyalty → Redemptions: recent transactions with member + reward embeds */
    redemptionsTransactions: (restaurantId: string) =>
      ["loyalty-redemptions-transactions", restaurantId] as const,
    /** Loyalty → Push campaigns list (tenant-scoped) */
    pushCampaigns: (restaurantId: string) =>
      ["loyalty-push", restaurantId] as const,
    /** Tiers for admin loyalty UIs when scoped by `restaurant_id` */
    tiers: (restaurantId: string) => ["loyalty-tiers", restaurantId] as const,
    /** Active vouchers for push composer (status = active) */
    pushActiveVouchers: (restaurantId: string) =>
      ["loyalty-vouchers-active", restaurantId] as const,
    /** Loyalty → Vouchers admin tab: promo rows (`code` set), tenant-scoped */
    vouchersAdmin: (restaurantId: string) =>
      ["loyalty-vouchers", restaurantId] as const,
    /** Loyalty → Gift cards admin tab (`gift_cards`, tenant-scoped) */
    giftCardsAdmin: (restaurantId: string) =>
      ["loyalty-gift-cards-admin", restaurantId] as const,
    /** Reachability stats for push targeting; tierIdsKey invalidates when tier set changes */
    pushMemberStats: (restaurantId: string, tierIdsKey: string) =>
      ["loyalty-member-stats-push", restaurantId, tierIdsKey] as const,
    /** Loyalty → Top-up: members + customer contact (tenant-scoped) */
    topUpMembers: (restaurantId: string) =>
      ["loyalty-members", restaurantId] as const,
    /** Loyalty → Members admin table (full row + customer + tier embeds); distinct from `topUpMembers` select shape */
    membersAdmin: (restaurantId: string) =>
      ["loyalty-members-admin", restaurantId] as const,
    /** Customers not yet enrolled in loyalty for this tenant (enroll dialog) */
    nonLoyaltyCustomers: (restaurantId: string) =>
      ["loyalty-non-member-customers", restaurantId] as const,
    /** Loyalty → Top-up: wallet top-up + spend ledger rows */
    topUpTransactions: (restaurantId: string) =>
      ["loyalty-transactions-topup", restaurantId] as const,
    /** Loyalty → Rewards catalog (admin) */
    rewards: (restaurantId: string) =>
      ["loyalty-rewards", restaurantId] as const,
    /** Loyalty → Program settings row (`loyalty_settings`, tenant-scoped) */
    settings: (restaurantId: string) =>
      ["loyalty-settings", restaurantId] as const,
  },
  orders: {
    list: (restaurantId: string, opts: { days?: number; status?: string }) =>
      ["orders", restaurantId, opts] as const,
    /** Admin orders table: joined invoice + payment method enrichment */
    adminList: (restaurantId: string) =>
      ["orders-admin", restaurantId] as const,
    detailItems: (orderId: string) => ["order-detail-items", orderId] as const,
    detailPayment: (orderId: string) =>
      ["order-detail-payment", orderId] as const,
  },
  restaurants: {
    /** Superadmin: merged `user_roles` + all `restaurants` (keyed per user for cache isolation). */
    impersonationList: (userId: string) =>
      ["restaurants-impersonation-list", userId] as const,
    receiptSettings: (restaurantId: string) =>
      ["restaurant-receipt-settings", restaurantId] as const,
    /** Full `restaurants` row for active tenant (Terminal, FontProvider, etc.). */
    settings: (restaurantId: string) =>
      ["restaurant-settings", restaurantId] as const,
    /** `restaurant_settings` row (order theme, scheduling, express kiosk). */
    settingsRow: (restaurantId: string) =>
      ["restaurant-settings-row", restaurantId] as const,
    /** `payment_settings` row (bank, Swish, toggles) — Settings page. */
    paymentSettings: (restaurantId: string) =>
      ["payment-settings", restaurantId] as const,
    bySlug: (slug: string) => ["restaurant-by-slug", slug] as const,
  },
  /** Admin VAT table (all rows); terminal uses `terminal.vatRates` (active only). */
  vat: {
    admin: (restaurantId: string) => ["vat-rates-admin", restaurantId] as const,
  },
  user: {
    restaurantMemberships: (userId: string) => ["user-roles", userId] as const,
    inactiveRestaurantMemberships: (userId: string) =>
      ["user-roles-inactive-count", userId] as const,
    profile: (userId: string) => ["profile", userId] as const,
  },
  /** Admin gift cards tab + terminal payment: shared invalidation */
  giftCards: {
    list: (restaurantId: string) => ["gift-cards", restaurantId] as const,
  },
  /** POS Terminal: VAT, parked order count, dishes by category */
  terminal: {
    vatRates: (restaurantId: string) =>
      ["terminal-vat-rates", restaurantId] as const,
    parkedCount: (restaurantId: string) =>
      ["terminal-parked-count", restaurantId] as const,
    dishesByCategory: (restaurantId: string, categoryId: string) =>
      ["terminal-dishes", restaurantId, categoryId] as const,
    /** Orders overview sheet: recent orders */
    ordersOverview: (restaurantId: string) =>
      ["terminal-orders-overview", restaurantId] as const,
    /** Orders overview sheet: active dine-in orders for table map */
    ordersOverviewActiveDineIn: (restaurantId: string) =>
      ["terminal-orders-overview-dine-in", restaurantId] as const,
    /** Terminal inventory tab: dishes with category name */
    inventoryDishes: (restaurantId: string) =>
      ["terminal-inventory-dishes", restaurantId] as const,
    /** X report dialog: today’s sales snapshot (dateKey = yyyy-MM-dd, UTC) */
    xReportToday: (restaurantId: string, dateKey: string, localeTag: string) =>
      ["terminal-x-report", restaurantId, dateKey, localeTag] as const,
    /** POS Terminal settings row (`terminal_settings`). */
    settings: (restaurantId: string) =>
      ["terminal-settings", restaurantId] as const,
    /** Customer link dialog: recent CRM rows */
    customerLinkRecent: (restaurantId: string) =>
      ["terminal-customer-link-recent", restaurantId] as const,
    /** Customer link dialog / checkout: search (q trimmed, >= 2 chars) */
    customerLinkSearch: (restaurantId: string, q: string, limit: number) =>
      ["terminal-customer-link-search", restaurantId, q, limit] as const,
    /** Customer link dialog: loyalty rows for ordered customer id list */
    customerLinkLoyalty: (restaurantId: string, idsKey: string) =>
      ["terminal-customer-link-loyalty", restaurantId, idsKey] as const,
  },
  /** Settings → hero slides & campaign banners (tenant-scoped) */
  settingsMarketing: {
    heroSlides: (restaurantId: string) =>
      ["settings-hero-slides", restaurantId] as const,
    campaignBanners: (restaurantId: string) =>
      ["settings-campaign-banners", restaurantId] as const,
  },
  /** Multi-location admin */
  locations: {
    list: (restaurantId: string) => ["locations", restaurantId] as const,
  },
  /** GDPR tools */
  gdpr: {
    requests: (restaurantId: string) =>
      ["gdpr-requests", restaurantId] as const,
    customerPicker: (restaurantId: string) =>
      ["gdpr-customers-picker", restaurantId] as const,
  },
  /** Happy hour rules admin */
  happyHour: {
    rules: (restaurantId: string) =>
      ["happy-hour-rules", restaurantId] as const,
    categories: (restaurantId: string) =>
      ["happy-hour-categories", restaurantId] as const,
    dishes: (restaurantId: string) =>
      ["happy-hour-dishes", restaurantId] as const,
  },
  /** Kitchen display: active order columns */
  kitchen: {
    activeOrders: (restaurantId: string) =>
      ["kitchen-orders", restaurantId] as const,
  },
  /** Guest table status page (keyed by tenant slug + reservation) */
  tableStatus: {
    byReservation: (slug: string, reservationId?: string) =>
      ["table-status", slug, reservationId || "_none_"] as const,
  },
  staff: {
    shiftsWeek: (restaurantId: string, weekStart: string) =>
      ["staff-shifts", restaurantId, weekStart] as const,
    profilesForRestaurant: (restaurantId: string) =>
      ["staff-profiles", restaurantId] as const,
    /** Schedule export dialog staff picker — distinct cache from `profilesForRestaurant`. */
    scheduleExportStaffList: (restaurantId: string) =>
      ["staff-list-export", restaurantId] as const,
    positions: (restaurantId: string) =>
      ["staff-positions", restaurantId] as const,
    weekRevenue: (restaurantId: string, weekStart: string) =>
      ["week-revenue", weekStart, restaurantId] as const,
    leaveRequests: (
      restaurantId: string,
      userId: string | null,
      isAdmin: boolean
    ) => ["leave-requests", restaurantId, userId, isAdmin] as const,
    timeClockProfile: (userId: string) =>
      ["staff-timeclock-profile", userId] as const,
    timeClockActive: (userId: string, restaurantId: string) =>
      ["my-active-entry", userId, restaurantId] as const,
    timeClockToday: (restaurantId: string) =>
      ["staff-timeclock-today", restaurantId] as const,
    timeClockMyEntries: (userId: string, restaurantId: string) =>
      ["my-entries", userId, restaurantId] as const,
    /** Time clock export dialog — `restaurants` row for display name. */
    timeClockExportRestaurant: (restaurantId: string) =>
      ["restaurant-settings-export", restaurantId] as const,
    timeClockExportEntries: (
      restaurantId: string,
      dateFrom: string,
      dateTo: string
    ) => ["time-clock-export-entries", restaurantId, dateFrom, dateTo] as const,
    /** RPC `restaurant_has_staff_pins` — PIN gate / staff POS PIN. */
    staffPinsConfigured: (restaurantId: string) =>
      ["staff-pins-configured", restaurantId] as const,
    /** `/admin` staff overview — invalidate prefix `["staff-overview", restaurantId]`. */
    overviewDayShifts: (restaurantId: string, date: string) =>
      ["staff-overview", restaurantId, "day-shifts", date] as const,
    overviewRangeShifts: (restaurantId: string, start: string, end: string) =>
      ["staff-overview", restaurantId, "range-shifts", start, end] as const,
    overviewActiveEntries: (restaurantId: string) =>
      ["staff-overview", restaurantId, "active-entries"] as const,
    overviewDayRevenue: (restaurantId: string, date: string) =>
      ["staff-overview", restaurantId, "day-revenue", date] as const,
    /** Auto-schedule: `staff_scheduling_rules` (dialog open). */
    schedulingRules: (restaurantId: string) =>
      ["scheduling-rules", restaurantId] as const,
    /** Auto-schedule: `staff_availability` (dialog open). */
    staffAvailability: (restaurantId: string) =>
      ["staff-availability", restaurantId] as const,
  },
  /** Admin `/admin/role-management`: tenant `user_roles` + profile picker list. */
  roleManagement: {
    userRoles: (restaurantId: string) =>
      ["role-management", "user-roles", restaurantId] as const,
    profilesPickList: () => ["role-management", "profiles-pick-list"] as const,
  },
  publicBooking: {
    /** Id-only lookup for public `/book/:slug` — avoids cache shape clash with `publicOrder.restaurantBySlug`. */
    restaurantBySlug: (slug: string) =>
      ["public-booking-restaurant", slug] as const,
    /** Tenant-scoped public booking data; key segment is `restaurants.id` (UUID), not URL slug. */
    reservationTypes: (restaurantId: string) =>
      ["reservation-types-public", restaurantId] as const,
    settings: (restaurantId: string) =>
      ["booking-settings-public", restaurantId] as const,
    restaurantSettings: (slug: string) =>
      ["restaurant-settings-public", slug] as const,
    existingBookings: (restaurantId: string, dateStr: string) =>
      ["existing-bookings", restaurantId, dateStr] as const,
    tables: (restaurantId: string) =>
      ["tables-for-booking", restaurantId] as const,
  },
  /** Public in-venue order status board (`/display/:slug`). */
  publicDisplay: {
    activeOrderQueue: (slug: string) =>
      ["customer-display-order-queue", slug] as const,
  },
  /** Public catering form (`/catering-inquiry?slug=…`); legacy global key when slug absent. */
  publicCatering: {
    restaurantSettings: (slugKey: string, restaurantId?: string | null) =>
      ["public-catering-settings", slugKey, restaurantId ?? ""] as const,
  },
  publicService: {
    categories: (slug: string) => ["public-service-categories", slug] as const,
    resources: (slug: string) => ["public-resources", slug] as const,
    bookings: (slug: string, dateStr: string, resourceId: string) =>
      ["public-bookings", slug, dateStr, resourceId] as const,
  },
  /** Public gift card viewer (`/gift/:slug/:code`) */
  publicGiftCard: {
    restaurant: (slug: string) =>
      ["public-gift-card-restaurant", slug] as const,
    card: (restaurantId: string, codeNormalized: string) =>
      ["public-gift-card-detail", restaurantId, codeNormalized] as const,
    transactions: (giftCardId: string) =>
      ["public-gift-card-transactions", giftCardId] as const,
  },
  publicOrder: {
    restaurantBySlug: (slug: string) =>
      ["public-order-restaurant", slug] as const,
    themeSettings: (restaurantId: string) =>
      ["public-order-theme-settings", restaurantId] as const,
    categories: (restaurantId: string) =>
      ["public-order-categories", restaurantId] as const,
    heroSlides: (restaurantId: string) =>
      ["public-order-hero-slides", restaurantId] as const,
    campaignBanners: (restaurantId: string) =>
      ["public-order-campaign-banners", restaurantId] as const,
    dishes: (restaurantId: string, categoryId: string) =>
      ["public-order-dishes", restaurantId, categoryId] as const,
    subcategories: (restaurantId: string, categoryId: string) =>
      ["public-order-subcategories", restaurantId, categoryId] as const,
    /** Public `/order/:slug/status/:orderNumber` and legacy `/order/:slug/status/:id` — invalidate `statusPageRoot` on realtime updates. */
    statusPageRoot: ["public-order-status-page"] as const,
    statusPageById: (orderId: string) =>
      ["public-order-status-page", "id", orderId] as const,
    orderStatusByNumber: (orderNumber: string) =>
      ["public-order-status-page", "number", orderNumber] as const,
    statusPageByRestaurantNumber: (restaurantId: string, orderNumber: number) =>
      ["public-order-status-page", "n", restaurantId, orderNumber] as const,
    /** `/order` type picker: active `order_types` rows (anon + RLS header scoped to restaurant). */
    activeOrderTypes: (restaurantId: string) =>
      ["public-order-active-order-types", restaurantId] as const,
    /** Online checkout dialog: pickup/delivery scheduling columns */
    checkoutScheduling: (slug: string) =>
      ["public-order-checkout-scheduling", slug] as const,
    /** Online checkout dialog: active dine-in tables */
    checkoutTables: (slug: string) =>
      ["public-order-checkout-tables", slug] as const,
    /** Public `/order/:slug/pay/:orderId?token=...` — validate token + payment_status */
    payCheckout: (slug: string, orderId: string, token: string) =>
      ["public-order-pay", slug, orderId, token] as const,
    /** Public `/order/:slug`: Swedbank terminal + online enable flags */
    paymentFlags: (restaurantId: string) =>
      ["public-order-payment-flags", restaurantId] as const,
    /** Order info sheet: restaurant row by slug */
    orderInfoRestaurant: (slug: string) =>
      ["public-order-info-restaurant", slug] as const,
    /** Order info sheet: `order_types` row by `type` + tenant */
    orderTypeMeta: (restaurantId: string, orderType: string) =>
      ["public-order-type-meta", restaurantId, orderType] as const,
    /** Express kiosk: initial categories + settings + all express dishes */
    expressBootstrap: (slug: string) => ["express-bootstrap", slug] as const,
    expressDishesByCategory: (slug: string, categoryId: string) =>
      ["express-dishes-category", slug, categoryId] as const,
    /** Add-ons + ingredients sheet for a dish (online order, express, terminal). */
    dishCustomizeBundle: (
      restaurantId: string,
      dishId: string,
      categoryId: string
    ) =>
      [
        "public-order-dish-customize",
        restaurantId,
        dishId,
        categoryId,
      ] as const,
  },
  /** Lunch designer + order flow: tenant-scoped dishes and daily lunch schedule */
  lunch: {
    availableDishes: (restaurantId: string) =>
      ["lunch-available-dishes", restaurantId] as const,
    scheduleDay: (restaurantId: string, scheduleDate: string) =>
      ["lunch-schedule-day", restaurantId, scheduleDate] as const,
    schedulesRange: (restaurantId: string, from: string, to: string) =>
      ["lunch-schedules", restaurantId, from, to] as const,
    recurringDishes: (restaurantId: string) =>
      ["lunch-recurring-dishes", restaurantId] as const,
    schedulePickerDishes: (restaurantId: string) =>
      ["lunch-schedule-picker-dishes", restaurantId] as const,
    /** Visual lunch menu designer: `saved_menu_designs` with `designer_type = visual`. */
    savedVisualMenuDesigns: (restaurantId: string) =>
      ["saved-menu-designs", restaurantId] as const,
  },
  /** Delivery / POS integrations (`integrations` table per restaurant). */
  integrations: {
    list: (restaurantId: string) => ["integrations", restaurantId] as const,
  },
  /** Admin table / floor / QR management */
  tables: {
    admin: (restaurantId: string) =>
      ["tables-management", restaurantId] as const,
    /** Live dine-in table cards + active orders (`TableOverview`). */
    overview: (restaurantId: string) =>
      ["tables-overview", restaurantId] as const,
  },
  /** Admin order types (dine-in, takeaway, etc.) */
  orderTypes: {
    admin: (restaurantId: string) =>
      ["order-types-admin", restaurantId] as const,
  },
  /** Admin services / appointments (categories, resources, bookings, calendar). */
  serviceAdmin: {
    categories: (restaurantId: string) =>
      ["service-admin", "categories", restaurantId] as const,
    bundle: (restaurantId: string) =>
      ["service-admin", "bundle", restaurantId] as const,
    resources: (restaurantId: string) =>
      ["service-admin", "resources", restaurantId] as const,
    resourceTypes: (restaurantId: string) =>
      ["service-admin", "resource-types", restaurantId] as const,
    bookingsAll: (restaurantId: string) =>
      ["service-admin", "bookings-all", restaurantId] as const,
    bookingsRange: (restaurantId: string, from: string, to: string) =>
      ["service-admin", "bookings-range", restaurantId, from, to] as const,
    servicesActive: (restaurantId: string) =>
      ["service-admin", "services-active", restaurantId] as const,
    resourcesActive: (restaurantId: string) =>
      ["service-admin", "resources-active", restaurantId] as const,
    bookingSettings: (restaurantId: string) =>
      ["service-admin", "booking-settings", restaurantId] as const,
  },
  /** Digital signage admin (devices, zones, playlist picklists) — keys include restaurantId for tenant cache isolation */
  signage: {
    /** Full device rows for admin devices / groups pages */
    devicesAdmin: (restaurantId: string) =>
      ["signage-devices", restaurantId] as const,
    /** Minimal id/name (and resolution) for selects and zone editor */
    devicesPicklist: (restaurantId: string) =>
      ["signage-devices-list", restaurantId] as const,
    deviceGroups: (restaurantId: string) =>
      ["signage-device-groups", restaurantId] as const,
    screenZones: (deviceId: string) => ["signage-zones", deviceId] as const,
    playlists: (restaurantId: string) =>
      ["signage-playlists", restaurantId] as const,
    schedules: (restaurantId: string) =>
      ["signage-schedules", restaurantId] as const,
    playbackLogs: (restaurantId: string, rangeDays: number) =>
      ["signage-playback-logs", restaurantId, rangeDays] as const,
    commandsRecent: (restaurantId: string) =>
      ["signage-commands-recent", restaurantId] as const,
    alerts: (restaurantId: string) => ["signage-alerts", restaurantId] as const,
    assets: (restaurantId: string, folderId: string | null) =>
      ["signage-assets", restaurantId, folderId ?? "root"] as const,
    assetFolders: (restaurantId: string, parentId: string | null) =>
      ["signage-asset-folders", restaurantId, parentId ?? "root"] as const,
    assetBreadcrumbs: (restaurantId: string, folderId: string) =>
      ["signage-asset-breadcrumbs", restaurantId, folderId] as const,
    assetsAll: (restaurantId: string) =>
      ["signage-assets-all", restaurantId] as const,
    playlistItems: (restaurantId: string, playlistId: string) =>
      ["signage-playlist-items", restaurantId, playlistId] as const,
  },
  /** On Demand (od_*) — tenant-scoped studio / credits / requests. */
  onDemand: {
    serviceCategories: (restaurantId: string, includeInactive: boolean) =>
      [
        "on-demand",
        "service-categories",
        restaurantId,
        includeInactive,
      ] as const,
    services: (restaurantId: string, includeInactive: boolean) =>
      ["on-demand", "services", restaurantId, includeInactive] as const,
    creditWallets: (restaurantId: string) =>
      ["on-demand", "credit-wallets", restaurantId] as const,
    creditTransactions: (restaurantId: string, walletId: string | null) =>
      ["on-demand", "credit-transactions", restaurantId, walletId] as const,
    requests: (restaurantId: string) =>
      ["on-demand", "requests", restaurantId] as const,
    projects: (restaurantId: string) =>
      ["on-demand", "projects", restaurantId] as const,
    milestones: (restaurantId: string, projectId: string | null) =>
      ["on-demand", "milestones", restaurantId, projectId] as const,
    deliverables: (restaurantId: string, projectId: string | null) =>
      ["on-demand", "deliverables", restaurantId, projectId] as const,
    comments: (restaurantId: string, projectId: string | null) =>
      ["on-demand", "comments", restaurantId, projectId] as const,
    roles: (restaurantId: string) =>
      ["on-demand", "roles", restaurantId] as const,
    activityLog: (restaurantId: string) =>
      ["on-demand", "activity-log", restaurantId] as const,
    brandProfiles: (restaurantId: string) =>
      ["on-demand", "brand-profiles", restaurantId] as const,
    packages: (restaurantId: string) =>
      ["on-demand", "packages", restaurantId] as const,
    userRoles: (restaurantId: string) =>
      ["on-demand", "user-roles", restaurantId] as const,
    /** All deliverables for tenant (joined to projects for RLS-safe scope). */
    allDeliverables: (restaurantId: string) =>
      ["on-demand", "all-deliverables", restaurantId] as const,
    request: (restaurantId: string, requestId: string | undefined) =>
      ["on-demand", "request", restaurantId, requestId] as const,
    requestComments: (restaurantId: string, requestId: string | undefined) =>
      ["on-demand", "request-comments", restaurantId, requestId] as const,
    requestApprovals: (restaurantId: string, requestId: string | undefined) =>
      ["on-demand", "request-approvals", restaurantId, requestId] as const,
    linkedProjectForRequest: (
      restaurantId: string,
      requestId: string | undefined
    ) => ["on-demand", "linked-project", restaurantId, requestId] as const,
    requestActivity: (restaurantId: string, requestId: string | undefined) =>
      ["on-demand", "request-activity", restaurantId, requestId] as const,
    customersForWallets: (restaurantId: string) =>
      ["on-demand", "customers-for-wallets", restaurantId] as const,
    kpiLinks: (restaurantId: string, projectId: string | null) =>
      ["on-demand", "kpi-links", restaurantId, projectId ?? "none"] as const,
    requestLinkedToProject: (
      restaurantId: string,
      requestId: string | null | undefined
    ) =>
      [
        "on-demand",
        "request-project-link",
        restaurantId,
        requestId ?? "none",
      ] as const,
    /** Full `od_*` rows for On Demand calendar (distinct from list hooks with joins). */
    calendarOdProjects: (restaurantId: string) =>
      ["on-demand", "calendar", "od-projects", restaurantId] as const,
    calendarOdRequests: (restaurantId: string) =>
      ["on-demand", "calendar", "od-requests", restaurantId] as const,
  },
  /** Inbox (team channels / standups). */
  inbox: {
    channels: (restaurantId: string) =>
      ["inbox", "channels", restaurantId] as const,
    messages: (
      restaurantId: string,
      channelId: string | null,
      threadKey: string
    ) => ["inbox", "messages", restaurantId, channelId, threadKey] as const,
    decisions: (restaurantId: string) =>
      ["inbox", "decisions", restaurantId] as const,
    standups: (restaurantId: string) =>
      ["inbox", "standups", restaurantId] as const,
    profiles: (restaurantId: string) =>
      ["inbox", "profiles", restaurantId] as const,
    analyticsMessages: (restaurantId: string, limit: number) =>
      ["inbox", "analytics-messages", restaurantId, limit] as const,
  },
  /** Admin Marketing Analytics: campaigns, budgets, ad platform connections. */
  marketing: {
    connections: (restaurantId: string) =>
      ["marketing-connections", restaurantId] as const,
    /** Prefix for invalidating all platform-filtered campaign queries. */
    campaignsPrefix: (restaurantId: string) =>
      ["marketing-campaigns", restaurantId] as const,
    campaigns: (restaurantId: string, platform?: string) =>
      ["marketing-campaigns", restaurantId, platform ?? "all"] as const,
    budgets: (restaurantId: string) =>
      ["marketing-budgets", restaurantId] as const,
    cachedData: (restaurantId: string, platform?: string) =>
      ["marketing-cached-data", restaurantId, platform ?? "all"] as const,
  },
  /** AI admin pages: input payloads built from Supabase (e.g. trend analysis). */
  ai: {
    trendAnalysisBase: (restaurantId: string, windowDays: number) =>
      ["ai-trend-analysis-base", restaurantId, windowDays] as const,
    recipeGeneratorContext: (restaurantId: string) =>
      ["ai-recipe-generator-context", restaurantId] as const,
    priceIntelligenceDishes: (restaurantId: string, windowDays: number) =>
      ["ai-price-intelligence-dishes", restaurantId, windowDays] as const,
    analysesHistory: (restaurantId: string, limit: number) =>
      ["ai-analyses-history", restaurantId, limit] as const,
    analysesByType: (restaurantId: string, analysisType: string) =>
      ["ai-analyses-by-type", restaurantId, analysisType] as const,
    dashboardSales: (restaurantId: string, windowDays: number, lang: string) =>
      ["ai-dashboard-sales", restaurantId, windowDays, lang] as const,
    menuOptimizerBundle: (
      restaurantId: string,
      windowDays: number,
      lang: string
    ) => ["ai-menu-optimizer-bundle", restaurantId, windowDays, lang] as const,
    wasteMenuData: (restaurantId: string, windowDays: number, lang: string) =>
      ["ai-waste-menu-data", restaurantId, windowDays, lang] as const,
    staffOptimizerBase: (restaurantId: string, windowDays: number) =>
      ["ai-staff-optimizer-base", restaurantId, windowDays] as const,
    customerInsightsBase: (
      restaurantId: string,
      windowDays: number,
      lang: string
    ) => ["ai-customer-insights-base", restaurantId, windowDays, lang] as const,
  },
  /** Admin `/admin/projects`, project detail sheet, and `/admin/todo`. */
  backofficeProjects: {
    list: (restaurantId: string) =>
      ["backoffice-projects", "list", restaurantId] as const,
    tasks: (restaurantId: string, projectId: string | null) =>
      ["backoffice-projects", "tasks", restaurantId, projectId] as const,
    budget: (restaurantId: string, projectId: string | null) =>
      ["backoffice-projects", "budget", restaurantId, projectId] as const,
    files: (restaurantId: string, projectId: string | null) =>
      ["backoffice-projects", "files", restaurantId, projectId] as const,
    comments: (restaurantId: string, projectId: string | null) =>
      ["backoffice-projects", "comments", restaurantId, projectId] as const,
  },
  todoBoard: {
    items: (restaurantId: string) =>
      ["todo-board", "items", restaurantId] as const,
  },
  /** Admin `/admin/quotes` + quote builder detail. */
  quotes: {
    list: (restaurantId: string) => ["quotes", restaurantId] as const,
    detail: (restaurantId: string, quoteId: string | null) =>
      ["quotes", "detail", restaurantId, quoteId] as const,
    items: (restaurantId: string, quoteId: string | null) =>
      ["quote-items", restaurantId, quoteId] as const,
    recipients: (restaurantId: string, quoteId: string | null) =>
      ["quote-recipients", restaurantId, quoteId] as const,
    /** Recipients for many quotes (e.g. quotes list grid). */
    recipientsByQuoteIds: (restaurantId: string, quoteIds: readonly string[]) =>
      ["quote-recipients", "by-quote-ids", restaurantId, quoteIds] as const,
    activities: (restaurantId: string, quoteId: string | null) =>
      ["quote-activities", restaurantId, quoteId] as const,
    comments: (restaurantId: string, quoteId: string | null) =>
      ["quote-comments", restaurantId, quoteId] as const,
  },
} as const;
